import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/app/AuthContext.jsx';
import {
  getQuoteByToken,
  acceptQuoteByToken,
  resendActivationRequest,
} from '@/lib/recepthor-api.js';
import OfertaView from './OfertaView.jsx';
import { readAndRemoveFragment, takeOfferToken } from './offerToken.js';
import { breakdownFromItems } from '@/lib/priceBreakdown.js';

export function offerErrorState(error) {
  if (error?.status === 429) return 'limited';
  if (error?.name === 'TokenExpired') return 'expired';
  if (
    [
      'InvalidQuoteToken',
      'TokenNotProvided',
      'AccessDenied',
      'QuoteNotFound',
    ].includes(error?.name)
  )
    return 'invalid';
  return 'error';
}

export default function Oferta() {
  const token = useRef();
  const generation = useRef(0);
  const [model, setModel] = useState({ state: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const [activation, setActivation] = useState('idle');
  const [notice, setNotice] = useState('');
  const submitting = useRef(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const replaceFromFragment = useCallback(() => {
    if (!window.location.hash) return;
    token.current = readAndRemoveFragment();
    generation.current += 1;
    setModel({ state: 'loading' });
    setActivation('idle');
    setNotice('');
    submitting.current = false;
    setAttempt((value) => value + 1);
  }, []);

  // Layout runs before paint. The ref survives StrictMode's effect replay.
  useLayoutEffect(() => {
    if (token.current === undefined) token.current = takeOfferToken();
  }, []);

  useLayoutEffect(() => {
    replaceFromFragment();
  }, [location.hash, replaceFromFragment]);

  useLayoutEffect(() => {
    window.addEventListener('hashchange', replaceFromFragment);
    window.addEventListener('popstate', replaceFromFragment);
    return () => {
      window.removeEventListener('hashchange', replaceFromFragment);
      window.removeEventListener('popstate', replaceFromFragment);
    };
  }, [replaceFromFragment]);

  useEffect(() => {
    let active = true;
    const currentGeneration = generation.current;
    if (!token.current) {
      setModel({ state: 'incomplete' });
      return;
    }
    setModel({ state: 'loading' });
    getQuoteByToken(token.current).then(
      (quote) => {
        if (!active || currentGeneration !== generation.current) return;
        // The open view needs the request to show anything; without it the
        // receptor would land on a result screen with no way out. The other
        // states only show their message.
        if (
          !quote?.receptor ||
          (quote.offerState === 'open' && !quote.request) ||
          !['open', 'offered', 'declined', 'closed'].includes(quote.offerState)
        ) {
          setModel({ state: 'error' });
          return;
        }
        setModel({ state: quote.offerState, quote });
      },
      (error) => {
        if (active && currentGeneration === generation.current)
          setModel({ state: offerErrorState(error) });
      },
    );
    return () => {
      active = false;
    };
  }, [attempt]);

  const sameReceptor =
    typeof auth?.user?.email === 'string' &&
    typeof model.quote?.receptor?.email === 'string' &&
    auth.user.email.toLowerCase() === model.quote.receptor.email.toLowerCase();
  const shouldRedirect =
    !auth?.loading &&
    auth?.isAuthenticated &&
    auth?.activeCapability === 'receptor' &&
    sameReceptor &&
    model.quote?.receptor?.hasOrganization;
  const waitingForSession =
    auth?.loading && model.quote?.receptor?.hasOrganization;

  useEffect(() => {
    if (shouldRedirect) {
      navigate(`/solicitudes/${encodeURIComponent(model.quote.quoteId)}`, {
        replace: true,
      });
    }
  }, [shouldRedirect, model.quote, navigate]);

  const resend = async () => {
    const currentGeneration = generation.current;
    setActivation('sending');
    let result;
    try {
      const response = await resendActivationRequest({
        email: model.quote.receptor.email,
      });
      result = response.ok ? 'sent' : 'error';
    } catch {
      result = 'error';
    }
    if (currentGeneration === generation.current) setActivation(result);
  };

  const submit = async (payload) => {
    if (submitting.current) return;
    const currentGeneration = generation.current;
    submitting.current = true;
    try {
      const response = await acceptQuoteByToken(token.current, payload);
      if (currentGeneration !== generation.current) return;
      setModel({
        state: 'offered',
        quote: {
          ...model.quote,
          offerState: 'offered',
          receptor: {
            ...model.quote.receptor,
            ...(payload.termsAccepted ? { accountActivated: false } : {}),
          },
          offer: {
            price: response?.quote?.price ?? payload.price,
            dateOffered: response?.quote?.dateOffered ?? payload.dateOffered,
            priceBreakdown:
              response?.quote?.priceBreakdown ??
              breakdownFromItems(payload.items),
          },
        },
      });
    } catch (error) {
      if (currentGeneration !== generation.current) return;
      if (error.name === 'QuoteAlreadyAnswered') {
        setNotice('Esta solicitud ya tiene tu respuesta.');
        setAttempt((value) => value + 1);
      } else if (error.name === 'TaskNotAcceptingOffers') {
        setModel({ state: 'closed' });
      } else if (
        offerErrorState(error) === 'invalid' ||
        offerErrorState(error) === 'expired'
      ) {
        setModel({ state: offerErrorState(error) });
      } else throw error;
    } finally {
      if (currentGeneration === generation.current) submitting.current = false;
    }
  };

  return (
    <OfertaView
      model={shouldRedirect || waitingForSession ? { state: 'loading' } : model}
      activation={activation}
      onResend={resend}
      onSubmit={submit}
      notice={notice}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}
